import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import axios from 'axios';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesKPIsWidget = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [marketId, setMarketId] = useState('');
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    fetchMarkets();
  }, []);

  useEffect(() => {
    fetchKPIs();
  }, [period, marketId]);

  const fetchMarkets = async () => {
    try {
      const response = await axios.get(`${API}/markets`);
      setMarkets(response.data);
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const fetchKPIs = async () => {
    try {
      const params = { period };
      if (marketId) params.market_id = marketId;
      
      const response = await axios.get(`${API}/director/sales-kpis`, { params });
      setKpiData(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${num.toFixed(0)}`;
  };

  const renderTrend = (value) => {
    if (!value) return null;
    const isPositive = value > 0;
    return (
      <div className={`flex items-center text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
        {Math.abs(value).toFixed(1)}%
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex gap-2">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[130px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">Quarter</SelectItem>
            <SelectItem value="year">Year</SelectItem>
          </SelectContent>
        </Select>

        {markets.length > 0 && (
          <Select value={marketId || "all"} onValueChange={(value) => setMarketId(value === "all" ? "" : value)}>
            <SelectTrigger className="w-[130px] h-8">
              <SelectValue placeholder="All Markets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Markets</SelectItem>
              {markets.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Total Jobs</div>
          <div className="flex justify-between items-end">
            <div className="text-2xl font-bold">{kpiData?.total_jobs || 0}</div>
          </div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Close Rate</div>
          <div className="flex justify-between items-end">
            <div className="text-2xl font-bold">{kpiData?.close_rate || 0}%</div>
            {renderTrend(kpiData?.trend_close_rate)}
          </div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Demo Rate</div>
          <div className="flex justify-between items-end">
            <div className="text-2xl font-bold">{kpiData?.demo_rate || 0}%</div>
          </div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Total Sales</div>
          <div className="flex justify-between items-end">
            <div className="text-2xl font-bold">{formatNumber(kpiData?.total_sales || 0)}</div>
            {renderTrend(kpiData?.trend_total_sales)}
          </div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Avg Sale</div>
          <div className="text-2xl font-bold">{formatNumber(kpiData?.average_sale || 0)}</div>
        </div>

        <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <div className="text-xs text-gray-600 mb-1">Speed to Lead</div>
          <div className="text-2xl font-bold">{kpiData?.speed_to_lead || 0}<span className="text-sm">m</span></div>
        </div>
      </div>

      {/* Lead Sources */}
      <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
        <div className="text-xs text-gray-600 mb-2">Lead Sources</div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <div className="font-semibold">{kpiData?.referral_leads || 0}</div>
            <div className="text-xs text-gray-600">Referral</div>
          </div>
          <div>
            <div className="font-semibold">{kpiData?.self_generated_leads || 0}</div>
            <div className="text-xs text-gray-600">Self-Gen</div>
          </div>
          <div>
            <div className="font-semibold">{kpiData?.company_issued_leads || 0}</div>
            <div className="text-xs text-gray-600">Company</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesKPIsWidget;